import { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, X, Zap, Crown, Building2, Rocket, Star, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Layout } from '@/components/layout/Layout';
import { useSubscription } from '@/hooks/useSubscription';
import { usePaymentProvider } from '@/hooks/usePaymentProvider';
import { useAuthStore } from '@/stores/authStore';
import { PLAN_LIMITS, PlanType, formatLimit } from '@/lib/plan-limits';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const planIcons: Record<PlanType, React.ReactNode> = {
  free: <Zap className="h-5 w-5" />,
  starter: <Rocket className="h-5 w-5" />,
  professional: <Star className="h-5 w-5" />,
  business: <Crown className="h-5 w-5" />,
  enterprise: <Building2 className="h-5 w-5" />,
};

const planColors: Record<PlanType, string> = {
  free: 'bg-muted text-muted-foreground',
  starter: 'bg-blue-500/10 text-blue-500',
  professional: 'bg-primary/10 text-primary',
  business: 'bg-amber-500/10 text-amber-500',
  enterprise: 'bg-emerald-500/10 text-emerald-500',
};

export default function Pricing() {
  const [isYearly, setIsYearly] = useState(true);
  const { plan: currentPlan, isLoading } = useSubscription();
  const { subscribe, isSubscribing, paymentConfig, isCheckingConfig } = usePaymentProvider();
  const { user } = useAuthStore();
  const navigate = useNavigate();

  const handleSubscribe = async (plan: PlanType) => {
    if (plan === 'enterprise') {
      window.location.href = 'mailto:sales@nexqr.online?subject=Enterprise%20Plan%20Inquiry';
      return;
    }

    if (plan === 'free') {
      toast.info('You are already on the free plan');
      return;
    }

    if (!user) {
      toast.info('Please sign in to subscribe');
      navigate('/auth');
      return;
    }

    if (!paymentConfig?.preferred) {
      toast.error('Payment not configured. Please contact support.');
      return;
    }

    // Subscribe using available payment provider
    subscribe(plan, isYearly);
  };

  const features = [
    { name: 'Static QR Codes', key: 'static', getValue: (p: PlanType) => formatLimit(PLAN_LIMITS[p].staticQRLimit) },
    { name: 'Dynamic QR Codes', key: 'dynamic', getValue: (p: PlanType) => formatLimit(PLAN_LIMITS[p].dynamicQRLimit) },
    { name: 'Monthly Scans', key: 'scans', getValue: (p: PlanType) => formatLimit(PLAN_LIMITS[p].scansPerMonth) },
    { name: 'Analytics History', key: 'analytics', getValue: (p: PlanType) => PLAN_LIMITS[p].analyticsHistoryDays === -1 ? 'Unlimited' : `${PLAN_LIMITS[p].analyticsHistoryDays} days` },
    { name: 'Batch QR Codes', key: 'batch', getValue: (p: PlanType) => formatLimit(PLAN_LIMITS[p].batchLimit) },
    { name: 'AI Design Suggestions', key: 'ai', getValue: (p: PlanType) => PLAN_LIMITS[p].aiSuggestionsPerMonth === -1 ? 'Unlimited' : PLAN_LIMITS[p].aiSuggestionsPerMonth === 0 ? '—' : `${PLAN_LIMITS[p].aiSuggestionsPerMonth}/mo` },
    { name: 'Download Formats', key: 'formats', getValue: (p: PlanType) => PLAN_LIMITS[p].downloadFormats.join(', ').toUpperCase() },
    { name: 'Team Members', key: 'team', getValue: (p: PlanType) => PLAN_LIMITS[p].teamMembers === -1 ? 'Unlimited' : PLAN_LIMITS[p].teamMembers === 1 ? 'Solo' : PLAN_LIMITS[p].teamMembers },
    { name: 'API Access', key: 'api', getValue: (p: PlanType) => PLAN_LIMITS[p].apiRequestsPerMonth === 0 ? '—' : PLAN_LIMITS[p].apiRequestsPerMonth === -1 ? 'Unlimited' : `${formatLimit(PLAN_LIMITS[p].apiRequestsPerMonth)}/mo` },
    { name: 'No Watermark', key: 'watermark', isBoolean: true, getValue: (p: PlanType) => !PLAN_LIMITS[p].features.watermark },
    { name: 'A/B Testing', key: 'abTesting', isBoolean: true, getValue: (p: PlanType) => PLAN_LIMITS[p].features.abTesting },
    { name: 'Custom Domain', key: 'customDomain', isBoolean: true, getValue: (p: PlanType) => PLAN_LIMITS[p].features.customDomain },
    { name: 'White Label', key: 'whiteLabel', isBoolean: true, getValue: (p: PlanType) => PLAN_LIMITS[p].features.whiteLabel },
    { name: 'Priority Support', key: 'prioritySupport', isBoolean: true, getValue: (p: PlanType) => PLAN_LIMITS[p].features.prioritySupport },
    { name: 'Dedicated Support', key: 'dedicatedSupport', isBoolean: true, getValue: (p: PlanType) => PLAN_LIMITS[p].features.dedicatedSupport },
    { name: 'SLA Guarantee', key: 'sla', isBoolean: true, getValue: (p: PlanType) => PLAN_LIMITS[p].features.slaGuarantee },
  ];

  const plans: PlanType[] = ['free', 'starter', 'professional', 'business', 'enterprise'];

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-8 md:py-14">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-3xl md:text-4xl font-black tracking-tight mb-3">Simple, Transparent Pricing</h1>
          <p className="text-lg text-muted-foreground mb-8">
            Choose the perfect plan for your QR code needs
          </p>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-4">
            <span className={cn('text-sm', !isYearly && 'font-semibold')}>Monthly</span>
            <Switch
              checked={isYearly}
              onCheckedChange={setIsYearly}
              aria-label="Toggle yearly billing"
            />
            <span className={cn('text-sm', isYearly && 'font-semibold')}>
              Yearly
              <Badge variant="secondary" className="ml-2">Save 17%</Badge>
            </span>
          </div>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-6 mb-16">
          {plans.map((planKey, index) => {
            const plan = PLAN_LIMITS[planKey];
            const isCurrentPlan = currentPlan === planKey;
            const isProfessional = planKey === 'professional';
            const price = isYearly ? plan.yearlyPrice : plan.monthlyPrice;
            const monthlyEquivalent = isYearly ? Math.round(plan.yearlyPrice / 12) : plan.monthlyPrice;

            return (
              <motion.div
                key={planKey}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={cn(
                  'relative rounded-2xl border p-6 flex flex-col',
                  isProfessional && 'border-primary shadow-lg ring-2 ring-primary/20',
                  isCurrentPlan && 'bg-muted/50'
                )}
              >
                {isProfessional && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 gradient-hero">
                    Most Popular
                  </Badge>
                )}

                {isCurrentPlan && (
                  <Badge variant="outline" className="absolute -top-3 right-4">
                    Current Plan
                  </Badge>
                )}

                <div className={cn('inline-flex items-center gap-2 px-3 py-1 rounded-full w-fit mb-4', planColors[planKey])}>
                  {planIcons[planKey]}
                  <span className="font-semibold">{plan.displayName}</span>
                </div>

                <div className="mb-6">
                  {planKey === 'enterprise' ? (
                    <div className="text-3xl font-bold">Custom</div>
                  ) : (
                    <>
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-bold">${monthlyEquivalent}</span>
                        <span className="text-muted-foreground">/mo</span>
                      </div>
                      {isYearly && planKey !== 'free' && (
                        <p className="text-sm text-muted-foreground">
                          ${price} billed yearly
                        </p>
                      )}
                    </>
                  )}
                </div>

                <ul className="space-y-3 mb-6 flex-1">
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-primary flex-shrink-0" />
                    <span>{formatLimit(plan.staticQRLimit)} static QR codes</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-primary flex-shrink-0" />
                    <span>{formatLimit(plan.dynamicQRLimit)} dynamic QR codes</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-primary flex-shrink-0" />
                    <span>{formatLimit(plan.scansPerMonth)} scans/month</span>
                  </li>
                  {plan.features.abTesting && (
                    <li className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-primary flex-shrink-0" />
                      <span>A/B testing</span>
                    </li>
                  )}
                  {plan.teamMembers > 1 && (
                    <li className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-primary flex-shrink-0" />
                      <span>{plan.teamMembers === -1 ? 'Unlimited' : plan.teamMembers} team members</span>
                    </li>
                  )}
                </ul>

                <Button
                  className={cn(
                    'w-full',
                    isProfessional && 'gradient-hero'
                  )}
                  variant={isProfessional ? 'default' : 'outline'}
                  onClick={() => handleSubscribe(planKey)}
                  disabled={isCurrentPlan || isLoading || isSubscribing || isCheckingConfig}
                >
                  {isSubscribing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : isCurrentPlan ? (
                    'Current Plan'
                  ) : planKey === 'enterprise' ? (
                    'Contact Sales'
                  ) : plan.trialDays > 0 ? (
                    `Start ${plan.trialDays}-Day Trial`
                  ) : (
                    'Get Started'
                  )}
                </Button>
              </motion.div>
            );
          })}
        </div>

        {/* Feature Comparison Table */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="overflow-x-auto"
        >
          <h2 className="text-2xl font-black tracking-tight text-center mb-8">Compare All Features</h2>
          
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b">
                <th className="text-left py-4 px-4 font-medium">Feature</th>
                {plans.map((planKey) => (
                  <th key={planKey} className="text-center py-4 px-4 font-medium">
                    <div className={cn('inline-flex items-center gap-2 px-3 py-1 rounded-full', planColors[planKey])}>
                      {planIcons[planKey]}
                      <span>{PLAN_LIMITS[planKey].displayName}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {features.map((feature, index) => (
                <tr key={feature.key} className={cn('border-b', index % 2 === 0 && 'bg-muted/30')}>
                  <td className="py-4 px-4 font-medium">{feature.name}</td>
                  {plans.map((planKey) => {
                    const value = feature.getValue ? feature.getValue(planKey) : formatLimit(PLAN_LIMITS[planKey].staticQRLimit);
                    const isBoolean = feature.isBoolean;
                    
                    return (
                      <td key={planKey} className="text-center py-4 px-4">
                        {isBoolean ? (
                          value ? (
                            <Check className="h-5 w-5 text-primary mx-auto" />
                          ) : (
                            <X className="h-5 w-5 text-muted-foreground/50 mx-auto" />
                          )
                        ) : (
                          <span className={cn(
                            value === '—' && 'text-muted-foreground/50'
                          )}>
                            {value}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>

        {/* FAQ or CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="text-center mt-16"
        >
          <h2 className="text-2xl font-black tracking-tight mb-4">Questions?</h2>
          <p className="text-muted-foreground mb-6">
            Need help choosing the right plan? Contact our team for personalized advice.
          </p>
          <Button variant="outline" asChild>
            <a href="mailto:support@nexqr.online">Contact Support</a>
          </Button>
        </motion.div>
      </div>
    </Layout>
  );
}
